import { FC } from 'react'

import styled from 'styled-components'
import { Skeleton } from 'antd'
import { SkeletonNodeProps } from 'antd/es/skeleton/Node'

import { MAX_MOBILE_WIDTH } from 'src/Styled/variables'
import {
  CampaignStatusEnum,
  TikTokCampaignPricingModelEnum,
} from 'src/Types/campaigns'
import { CampaignPreModerationVideo } from 'src/Redux/campaignVideosApi/mapVideoToPreModerationStatus'
import { CampaignPayment } from 'src/Redux/artistOrderApi/campaignPayment'

import { StatsBlockContainer } from '../StatsBlockContainer'
import { InfoBlock } from '../InfoBlock'
import { VideosList } from '../VideosList'
import { ExpectationCards } from '../ExpectationCards'
import { CampaignGoal } from '../CampaignGoal'

import {
  getCampaignGoalValues,
  getShowExpectationCards,
  hasVideoPassedPreModeration,
} from './helpers'

const ResultWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 1 0 0;
  align-self: stretch;

  @media (max-width: ${MAX_MOBILE_WIDTH}) {
    gap: 48px;
  }
`

const StatsWrapper = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 16px;
  flex: 0 0 0;
  align-self: stretch;
`

const StyledSkeleton = styled(({ className, ...rest }: SkeletonNodeProps) => (
  <Skeleton.Input {...rest} rootClassName={className} active />
))`
  &.ant-skeleton-block {
    border-radius: 16px;
  }
  &.ant-skeleton {
    flex-grow: 1 !important;
    @media (max-width: ${MAX_MOBILE_WIDTH}) {
      width: 100% !important;
    }
  }
  & > .ant-skeleton-input {
    width: 100% !important;

    height: 240px !important;
    border-radius: 16px !important;
    background: ${({ theme }) => theme.color.lightGrey} !important;

    @media (max-width: ${MAX_MOBILE_WIDTH}) {
      width: 100% !important;
    }
  }
`

interface ResultSectionProps {
  loading: boolean
  viewsCount?: number
  commentsCount?: number
  campaignStatus?: CampaignStatusEnum
  campaignId?: number
  payment?: CampaignPayment | null
  likesCount?: number
  videosCount?: number
  videos?: Array<CampaignPreModerationVideo>
  startDate?: string
  onStartCampaignClick?: () => void
  pricingModel?: TikTokCampaignPricingModelEnum
  expectedMaxVideos?: number
  expectedMaxViews?: string
  expectedMinVideos?: number
  expectedMinViews?: string
}

export const ResultSection: FC<ResultSectionProps> = ({
  loading,
  viewsCount,
  commentsCount,
  campaignStatus,
  campaignId,
  payment,
  likesCount,
  videosCount,
  videos,
  startDate,
  onStartCampaignClick,
  pricingModel,
  expectedMaxVideos,
  expectedMaxViews,
  expectedMinVideos,
  expectedMinViews,
}) => {
  if (loading) {
    return <StyledSkeleton />
  }

  if (!campaignStatus) {
    return null
  }

  const showCampaignGoal = hasVideoPassedPreModeration(videos, campaignStatus)

  const showExpectationCards = getShowExpectationCards(
    pricingModel,
    campaignStatus,
    videosCount,
    videos,
  )

  const { currentValue, minValue, maxValue, calculatedPercentage } =
    getCampaignGoalValues(
      pricingModel,
      viewsCount,
      videosCount,
      expectedMinViews,
      expectedMinVideos,
      expectedMaxViews,
      expectedMaxVideos,
    )

  return (
    <ResultWrapper>
      <StatsWrapper>
        {showCampaignGoal && (
          <CampaignGoal
            pricingModel={pricingModel}
            currentValue={currentValue}
            minValue={minValue}
            maxValue={maxValue}
            percentage={calculatedPercentage}
          />
        )}
        <StatsBlockContainer
          viewsCount={viewsCount}
          commentsCount={commentsCount}
          likesCount={likesCount}
          videosCount={videosCount}
          campaignStatus={campaignStatus}
          pricingModel={pricingModel}
        />
        <InfoBlock
          startDate={startDate}
          campaignStatus={campaignStatus}
          campaignId={campaignId}
          payment={payment}
          videosCount={videosCount}
          onStartCampaignClick={onStartCampaignClick}
          compact={showExpectationCards}
        />
        {showExpectationCards && <ExpectationCards />}
      </StatsWrapper>

      <VideosList campaignStatus={campaignStatus} videos={videos} />
    </ResultWrapper>
  )
}
